import { useEffect, useRef, useState } from "react";
import { getAudio } from "@/audio";
import { startLullaby, stopLullaby } from "@/lullaby";
import { Slider } from "@/components/ui/slider";

export const fmtLeft = (ms) => {
  const s = Math.ceil(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const mm = String(m).padStart(h ? 2 : 1, "0"), ss = String(sec).padStart(2, "0");
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

export const SleepFade = ({ mins, spotifyRef, onTick, onWake }) => {
  const [op, setOp] = useState(0);
  const [night, setNight] = useState(0);
  const opRef = useRef(0);
  const secs = mins * 60 * (window.__sleepFadeScale || 1);

  useEffect(() => {
    const { ctx, out } = getAudio();
    const now = ctx.currentTime;
    out.gain.cancelScheduledValues(now);
    out.gain.setValueAtTime(out.gain.value, now);
    out.gain.linearRampToValueAtTime(0, now + secs * (1 - opRef.current));
    const from = opRef.current, start = Date.now(), total = secs * 1000 * (1 - from), end = start + total;
    window.__sleepFadeEndsAt = end;
    window.__sleepFadeDone = false;
    let id;
    let lullabyStarted = false;
    const tick = () => {
      const f = total ? Math.min(1, (Date.now() - start) / total) : 1;
      opRef.current = from + (1 - from) * f;
      setOp(opRef.current);
      const left = Math.max(0, end - Date.now());
      onTick(left);
      const nightWindow = 30000 * (window.__sleepFadeScale || 1);
      setNight(left > 0 && left <= nightWindow ? Math.sin(Math.PI * (1 - left / nightWindow)) : 0);
      const lastMinute = 60000 * (window.__sleepFadeScale || 1);
      if (!lullabyStarted && left > 0 && left <= lastMinute && window.__lullabyMode !== "sleep") {
        lullabyStarted = true;
        startLullaby({ seconds: left / 1000, direct: true });
      }
      if (f < 1) return;
      clearInterval(id);
      try { if (spotifyRef.current?.__playing) spotifyRef.current.pause(); } catch { /* nothing loaded */ }
      window.__sleepFadeDone = true;
    };
    tick();
    id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [secs, spotifyRef, onTick]);

  useEffect(() => () => {
    if (window.__lullabyMode === "sleep") stopLullaby();
    const { ctx, out } = getAudio();
    const t = ctx.currentTime;
    out.gain.cancelScheduledValues(t);
    out.gain.setValueAtTime(out.gain.value, t);
    out.gain.linearRampToValueAtTime(1, t + 2);
    window.__sleepFadeDone = false;
  }, []);

  return (
    <div className="sleep-night" data-testid="sleep-fade-overlay" data-mins={mins}
      style={{ opacity: op, transition: "opacity 0.5s linear", pointerEvents: op > 0.6 ? "auto" : "none" }} onPointerDown={() => op > 0.6 && onWake()}>
      <span className="goodnight" data-testid="goodnight-message" style={{ opacity: night }}>goodnight</span>
    </div>
  );
};

export const NIGHT_RAIN = "spotify:playlist:37i9dQZF1DXbcPC6Vvqudd";
const CALM = [
  { name: "Sleep", uri: "spotify:playlist:37i9dQZF1DWZd79rJ6a7lp" },
  { name: "Peaceful Piano", uri: "spotify:playlist:37i9dQZF1DX4sWSpwq3LiO" },
  { name: "Deep Sleep", uri: "spotify:playlist:37i9dQZF1DWYcDQ1hSjOpY" },
  { name: "Night Rain", uri: NIGHT_RAIN },
];

export const SleepPanel = ({ sleep, mins, setMins, leftMs, onToggle, onPickPlaylist }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  return (
  <aside ref={ref} className="sleep-panel" data-sleeping={sleep} data-open={open} data-testid="sleep-panel">
    <span className="spn-label">{sleep ? "asleep in" : "sleep countdown"}</span>
    <button className="spn-time" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="sleep-countdown">{fmtLeft(sleep ? leftMs : mins * 60000)}</button>
    <div className="spn-controls">
      <span className="spn-sub">fade over <b data-testid="sleep-length-value">{mins}</b> min</span>
      <Slider className="spn-slider" min={5} max={120} step={5} value={[mins]} onValueChange={([v]) => setMins(v)}
        aria-label="How slowly the site goes to sleep" data-testid="sleep-length-slider" />
      <div className="spn-scale"><span>5m</span><span>2h</span></div>
      {!sleep && (
        <div className="spn-playlists" data-testid="sleep-playlist-picker">
          <span className="spn-sub">sleep to</span>
          {CALM.map((p) => (
            <button key={p.uri} className="spn-pl" onClick={() => onPickPlaylist(p.uri)} data-testid={`sleep-playlist-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
              {p.name}
            </button>
          ))}
        </div>
      )}
      <button className="spn-btn" onClick={onToggle} data-testid="sleep-panel-toggle">{sleep ? "wake up" : "start sleep"}</button>
    </div>
  </aside>
  );
};
